import React, { useCallback, useEffect, useState } from "react";
import DataTable from "../components/common/DataTable";
import StatusBadge from "../components/common/StatusBadge";
import { useAdminApi } from "../hooks/useAdminApi";
import { UserSummary } from "../types/admin";

interface UserRow {
	id: number;
	external_id?: string;
	email: string;
	name?: string;
	created_at?: string | null;
}

const UsersPage: React.FC = () => {
	const api = useAdminApi();
	const [searchInput, setSearchInput] = useState("");
	const [keyword, setKeyword] = useState("");
	const [rows, setRows] = useState<UserRow[]>([]);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const columns = [
		{ key: "external_id", header: "User ID" },
		{ key: "email", header: "メールアドレス" },
		{ key: "name", header: "氏名" },
		{ key: "created_at", header: "作成日" },
	];

	const fetchUsers = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const res = await api.listUsers({
				email: keyword || undefined,
				external_id: keyword || undefined,
				page,
				per_page: pageSize,
			});

			const mapped: UserRow[] = res.data.map((u: UserSummary) => ({
				id: u.id,
				external_id: u.external_id ?? String(u.id),
				email: u.email,
				name:
					u.name?.display_name ||
					[u.name?.last_name, u.name?.first_name].filter(Boolean).join(" "),
				created_at: u.created_at,
			}));

			setRows(mapped);
			setTotal(res.total);
			setPageSize(res.per_page);
		} catch (err) {
			console.warn("Failed to load users", err);
			setError("ユーザー一覧の取得に失敗しました");
		} finally {
			setLoading(false);
		}
	}, [api, keyword, page, pageSize]);

	useEffect(() => {
		fetchUsers();
	}, [fetchUsers]);

	const handleSearch = () => {
		setPage(1);
		setKeyword(searchInput.trim());
	};

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-xl font-semibold text-ink">Users</h1>
					<p className="text-sm text-muted">
						メール/ID で検索、最新住所・氏名を表示
					</p>
				</div>
				<StatusBadge label="read only" tone="gray" />
			</div>

			<div className="rounded-2xl border border-surface-border bg-white p-4 shadow-card">
				<div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
					<div className="flex flex-1 gap-2">
						<input
							value={searchInput}
							onChange={(e) => setSearchInput(e.target.value)}
							className="flex-1 rounded-xl border border-surface-border bg-surface-alt px-3 py-2 text-sm focus:border-primary focus:outline-none"
							placeholder="メール or external_id"
						/>
						<button
							className="rounded-xl bg-primary px-3 sm:px-4 py-2 text-sm font-semibold text-white"
							onClick={handleSearch}
						>
							検索
						</button>
					</div>
					<div className="text-xs text-muted">
						詳細 API は /api/admin/users/{"{id}"}
					</div>
				</div>

				<div className="mt-4">
					<DataTable
						data={rows}
						columns={columns}
						page={page}
						pageSize={pageSize}
						total={total}
						onPageChange={setPage}
						loading={loading}
						error={error}
					/>
				</div>
			</div>
		</div>
	);
};

export default UsersPage;
